import { Badge } from "@/components/ui/badge";
import { MessageStatus } from "@common/models/message-log";
import { Clock, Check, CheckCheck, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { deriveMessageStatus, type DerivedMessageStatus } from "@/bt-common/services/message-status";
import { badgeVisualFromDerived, type BadgeIconName, type BadgeTone } from "./message-badge-visual";
import { CAMPAIGN_STATUS_COLORS, CAMPAIGN_STATUS_QUEUED_COLOR } from "@common/services/campaign-status-colors";

type ExtendedMessageStatus = MessageStatus | 'confirmed' | 'received' | 'expired';

interface MessageLogStatusBadgeProps {
    /** Raw persisted status for a SINGLE log — projected via bt-common's
     *  deriveMessageStatus. Ignored when `derived` is supplied. */
    status?: ExtendedMessageStatus;
    /** WhatsApp ack integer (0-4) for the log's receipt axis. Omit when unknown. */
    ack?: number;
    /** Recipient is a WhatsApp group (chatId ends with `@g.us`) — group receipts are not_reported. */
    isGroup?: boolean;
    /** Pre-derived two-axis status (rep-pick + cross-row receipt aggregation done by
     *  the caller, e.g. MessageStatusWithError). Overrides status/ack/isGroup. */
    derived?: DerivedMessageStatus;
    className?: string;
}

const ICON_BY_NAME: Record<BadgeIconName, React.ComponentType<{ className?: string }>> = {
    clock: Clock,
    check: Check,
    checkcheck: CheckCheck,
    xcircle: XCircle,
};

// Tone → colour from the SINGLE source of truth (CAMPAIGN_STATUS_COLORS), so the badges
// match the step-meter bar, the logs tiles, and the top-bar badge. sent = dark grey,
// delivered = green, read = LIGHT green, failed = red, queued = slate.
const COLOR_BY_TONE: Record<BadgeTone, string> = {
    queued: CAMPAIGN_STATUS_QUEUED_COLOR,
    failed: CAMPAIGN_STATUS_COLORS.failed,
    read: CAMPAIGN_STATUS_COLORS.read,
    delivered: CAMPAIGN_STATUS_COLORS.delivered,
    sent: CAMPAIGN_STATUS_COLORS.sending,
};

/**
 * Two-axis (wire × receipt) status renderer, driven entirely by bt-common's
 * canonical reader `deriveMessageStatus` (via the pure `badgeVisualFromDerived`
 * mapping). `confirmed` is FIRST-CLASS: a message WhatsApp's servers acknowledged
 * reads "Confirmed", not "Sent". Callers either pass a raw single-log
 * (status/ack/isGroup) or a pre-aggregated `derived` (representative row + the
 * cross-row receipt) — the latter keeps the per-message badge in agreement with
 * the aggregate stats, which resolve the same representative row via statusRank.
 */
export function MessageLogStatusBadge({ status, ack, isGroup = false, derived, className }: MessageLogStatusBadgeProps) {
    const resolved = derived ?? deriveMessageStatus(status ?? '', ack, isGroup);
    const { label, icon, tone } = badgeVisualFromDerived(resolved);
    const Icon = ICON_BY_NAME[icon];

    const color = COLOR_BY_TONE[tone];
    return (
        <Badge
            className={cn('text-white', className)}
            style={{ backgroundColor: color, borderColor: color }}
            variant="outline"
        >
            <Icon className="h-3 w-3" />
            <span>{label}</span>
        </Badge>
    );
}
